import type {
  OfflineArea,
  OfflineBooking,
  OfflineBootstrapBundle,
  OfflineCatalogItem,
  OfflineOrder,
  OfflinePricingSnapshot,
  OfflinePosContext,
  OfflineSalesTable,
  OfflineSession,
  OfflineVoucher,
} from './contracts'
import type { BigSaleDB } from './db'
import { buildOfflineContextKey } from './device-id'
import { http } from '@/plugins/axios'
import { apiEndpoints, buildApiPath } from '@/shared/constants/api'
import type { ApiResponseEnvelope } from '@/shared/types/api.type'

export type OfflineBootstrapDownloadResponse =
  | OfflineBootstrapBundle
  | ApiResponseEnvelope<OfflineBootstrapBundle>
export type OfflineBootstrapDownloader = () => Promise<OfflineBootstrapDownloadResponse>

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SIGNATURE_PATTERN = /^[0-9a-f]{64}$/i
const SIGNED_PAYLOAD_KEYS = [
  'branch_id',
  'branch_timezone',
  'bundle_id',
  'catalog_prices',
  'generated_at',
  'pricing_contract_version',
  'pricing_snapshots',
  'schema_version',
  'session_item_prices',
  'session_pricing_snapshots',
  'tenant_code',
  'vouchers',
] as const
const SIGNED_TABLE_PRICING_KEYS = ['table_id', 'bootstrap_id', 'pricing_snapshot'] as const
const SIGNED_SESSION_PRICING_KEYS = ['session_id', 'table_id', 'pricing_snapshot'] as const
const SIGNED_SESSION_ITEM_PRICE_KEYS = [
  'session_id',
  'order_item_id',
  'product_id',
  'unit_id',
  'price_list_id',
  'original_unit_price',
  'track_inventory',
] as const
const LEGACY_SIGNED_SESSION_ITEM_PRICE_KEYS = [
  'session_id',
  'order_item_id',
  'product_id',
  'unit_id',
  'original_unit_price',
] as const
const LEGACY_SIGNED_SESSION_ITEM_PRICE_KEYS_WITH_TRACKING = [
  ...LEGACY_SIGNED_SESSION_ITEM_PRICE_KEYS,
  'track_inventory',
] as const
const SIGNED_CATALOG_PRICE_KEYS = [
  'table_id',
  'product_id',
  'unit_id',
  'price_list_id',
  'effective_price',
  'allow_discount',
  'track_inventory',
  'sellable',
] as const
const LEGACY_SIGNED_CATALOG_PRICE_KEYS = SIGNED_CATALOG_PRICE_KEYS.filter(
  (key) => key !== 'price_list_id',
)

function invalidBundle(): never {
  throw new Error('OFFLINE_BUNDLE_INVALID')
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isPositiveInteger(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isInteger(value) && value > 0
}

function isNullablePositiveInteger(value: unknown): value is number | null {
  return value === null || isPositiveInteger(value)
}

function isNonNegativeNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

function isTimestamp(value: unknown): value is string {
  return isNonEmptyString(value) && Number.isFinite(Date.parse(value))
}

// Bundles generated before joined_at/left_at/settled_at were added omit these keys; treat
// that the same as null so the mapping (`?? null`) stays backward-compatible.
function isOptionalTimestamp(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || isTimestamp(value)
}

function deepEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => deepEqual(value, right[index]))
    )
  }

  if (!isRecord(left) || !isRecord(right)) {
    return false
  }

  const leftKeys = Object.keys(left).sort()
  const rightKeys = Object.keys(right).sort()

  return deepEqual(leftKeys, rightKeys) && leftKeys.every((key) => deepEqual(left[key], right[key]))
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return deepEqual(Object.keys(record).sort(), [...keys].sort())
}

function sessionItemPricesEqual(
  expected: OfflineBootstrapBundle['signed_payload']['session_item_prices'],
  actual: OfflineBootstrapBundle['signed_payload']['session_item_prices'],
): boolean {
  return (
    expected.length === actual.length &&
    expected.every((expectedItem, index) => {
      const actualItem = actual[index]
      if (actualItem.track_inventory !== undefined) {
        if (actualItem.price_list_id === undefined) {
          const { price_list_id: _priceListId, ...withoutPriceList } = expectedItem
          return deepEqual(withoutPriceList, actualItem)
        }
        return deepEqual(expectedItem, actualItem)
      }

      const {
        track_inventory: _trackInventory,
        price_list_id: _priceListId,
        ...legacyExpected
      } = expectedItem
      return deepEqual(legacyExpected, actualItem)
    })
  )
}

function assertCompatibleBundle(bundle: OfflineBootstrapBundle): void {
  try {
    const signed = bundle.signed_payload
    const branchId = bundle.branch.id

    if (
      bundle.schema_version !== 2 ||
      bundle.pricing_contract_version !== 1 ||
      signed.schema_version !== 2 ||
      signed.pricing_contract_version !== 1 ||
      !UUID_PATTERN.test(bundle.bundle_id) ||
      !SIGNATURE_PATTERN.test(bundle.signature) ||
      !isNonEmptyString(bundle.tenant_code) ||
      !isTimestamp(bundle.generated_at) ||
      !isTimestamp(bundle.server_time) ||
      !isNonEmptyString(bundle.branch_timezone) ||
      !isPositiveInteger(branchId) ||
      !isNullableString(bundle.branch.code) ||
      !isNonEmptyString(bundle.branch.name) ||
      !isPositiveInteger(bundle.actor_user_id) ||
      !Array.isArray(bundle.permission_codes) ||
      bundle.permission_codes.some((code) => !isNonEmptyString(code)) ||
      !isRecord(signed) ||
      !hasExactKeys(signed, SIGNED_PAYLOAD_KEYS) ||
      signed.bundle_id !== bundle.bundle_id ||
      signed.tenant_code !== bundle.tenant_code ||
      signed.branch_id !== branchId ||
      signed.generated_at !== bundle.generated_at ||
      signed.branch_timezone !== bundle.branch_timezone ||
      !Array.isArray(bundle.areas) ||
      !Array.isArray(bundle.tables) ||
      !Array.isArray(bundle.sessions) ||
      !Array.isArray(bundle.catalog) ||
      !Array.isArray(bundle.customers) ||
      !Array.isArray(bundle.vouchers) ||
      (bundle.bank_accounts !== undefined && !Array.isArray(bundle.bank_accounts)) ||
      (bundle.print_templates !== undefined && !Array.isArray(bundle.print_templates)) ||
      (bundle.bookings !== undefined && !Array.isArray(bundle.bookings)) ||
      (bundle.staff !== undefined && !Array.isArray(bundle.staff)) ||
      (bundle.point_policy !== undefined &&
        bundle.point_policy !== null &&
        typeof bundle.point_policy !== 'object') ||
      !Array.isArray(bundle.pricing_snapshots) ||
      !Array.isArray(signed.pricing_snapshots) ||
      !Array.isArray(signed.session_pricing_snapshots) ||
      !Array.isArray(signed.session_item_prices) ||
      !Array.isArray(signed.catalog_prices) ||
      !Array.isArray(signed.vouchers)
    ) {
      invalidBundle()
    }

    const voucherIds = new Set<number>()
    const voucherCodes = new Set<string>()
    for (const voucher of bundle.vouchers) {
      if (
        !isPositiveInteger(voucher.id) ||
        voucherIds.has(voucher.id) ||
        !isNonEmptyString(voucher.code) ||
        voucherCodes.has(voucher.code) ||
        !isNonEmptyString(voucher.name) ||
        !['percent', 'fixed'].includes(voucher.discount_type) ||
        !isNonNegativeNumber(voucher.discount_value) ||
        !isNonNegativeNumber(voucher.min_order_amount) ||
        !['unconditional', 'minimum_order'].includes(voucher.condition_type) ||
        !['table', 'products', 'all'].includes(voucher.discount_scope) ||
        !['all_items', 'selected_products', 'selected_groups'].includes(
          voucher.product_scope_mode,
        ) ||
        !isTimestamp(voucher.valid_from) ||
        (voucher.valid_until != null && !isTimestamp(voucher.valid_until)) ||
        voucher.status !== 'active' ||
        !['all', 'assigned', 'first_purchase'].includes(voucher.customer_scope) ||
        !['all', 'selected'].includes(voucher.branch_scope) ||
        !Array.isArray(voucher.branch_ids) ||
        voucher.branch_ids.some((id) => !isPositiveInteger(id))
      )
        invalidBundle()
      voucherIds.add(voucher.id)
      voucherCodes.add(voucher.code)
    }
    if (!deepEqual(bundle.vouchers, signed.vouchers)) invalidBundle()

    const areaIds = new Set<number>()
    for (const area of bundle.areas) {
      if (
        !isPositiveInteger(area.id) ||
        areaIds.has(area.id) ||
        area.branch_id !== branchId ||
        !isNullableString(area.code) ||
        !isNonEmptyString(area.name) ||
        typeof area.status !== 'boolean' ||
        !isFiniteNumber(area.sort_order) ||
        !isNullablePositiveInteger(area.price_list_id) ||
        !isNullablePositiveInteger(area.area_pricing_policy_id)
      ) {
        invalidBundle()
      }
      areaIds.add(area.id)
    }

    const tableIds = new Set<number>()
    const tableStatuses = new Map<number, OfflineSalesTable['status']>()
    for (const table of bundle.tables) {
      if (
        !isPositiveInteger(table.id) ||
        tableIds.has(table.id) ||
        table.branch_id !== branchId ||
        !isPositiveInteger(table.area_id) ||
        !areaIds.has(table.area_id) ||
        !isNullableString(table.code) ||
        !isNonEmptyString(table.name) ||
        !isNonNegativeNumber(table.seats) ||
        !['available', 'playing', 'reserved', 'cleaning'].includes(table.status) ||
        !isFiniteNumber(table.sort_order) ||
        typeof table.use_area_pricing !== 'boolean' ||
        !isNullablePositiveInteger(table.area_pricing_policy_override_id)
      ) {
        invalidBundle()
      }
      tableIds.add(table.id)
      tableStatuses.set(table.id, table.status)
    }

    const pricingTableIds = new Set<number>()
    for (const snapshot of bundle.pricing_snapshots) {
      if (
        !isRecord(snapshot) ||
        !hasExactKeys(snapshot, SIGNED_TABLE_PRICING_KEYS) ||
        !isPositiveInteger(snapshot.table_id) ||
        !tableIds.has(snapshot.table_id) ||
        pricingTableIds.has(snapshot.table_id) ||
        snapshot.bootstrap_id !== bundle.bundle_id ||
        !isRecord(snapshot.pricing_snapshot)
      ) {
        invalidBundle()
      }
      pricingTableIds.add(snapshot.table_id)
    }
    for (const snapshot of signed.pricing_snapshots) {
      if (!isRecord(snapshot) || !hasExactKeys(snapshot, SIGNED_TABLE_PRICING_KEYS)) {
        invalidBundle()
      }
    }
    for (const snapshot of signed.session_pricing_snapshots) {
      if (!isRecord(snapshot) || !hasExactKeys(snapshot, SIGNED_SESSION_PRICING_KEYS)) {
        invalidBundle()
      }
    }
    for (const snapshot of signed.session_item_prices) {
      if (
        !isRecord(snapshot) ||
        (!hasExactKeys(snapshot, SIGNED_SESSION_ITEM_PRICE_KEYS) &&
          !hasExactKeys(snapshot, LEGACY_SIGNED_SESSION_ITEM_PRICE_KEYS_WITH_TRACKING) &&
          !hasExactKeys(snapshot, LEGACY_SIGNED_SESSION_ITEM_PRICE_KEYS)) ||
        ('track_inventory' in snapshot && typeof snapshot.track_inventory !== 'boolean')
      ) {
        invalidBundle()
      }
    }
    for (const snapshot of signed.catalog_prices) {
      if (
        !isRecord(snapshot) ||
        (!hasExactKeys(snapshot, SIGNED_CATALOG_PRICE_KEYS) &&
          !hasExactKeys(snapshot, LEGACY_SIGNED_CATALOG_PRICE_KEYS))
      ) {
        invalidBundle()
      }
    }
    if (
      pricingTableIds.size !== tableIds.size ||
      !deepEqual(bundle.pricing_snapshots, signed.pricing_snapshots)
    ) {
      invalidBundle()
    }

    const customerIds = new Set<number>()
    for (const customer of bundle.customers) {
      if (
        !isPositiveInteger(customer.id) ||
        customerIds.has(customer.id) ||
        !isNullableString(customer.code) ||
        !isNonEmptyString(customer.name) ||
        !isNonEmptyString(customer.phone) ||
        !isNonEmptyString(customer.phone_normalized) ||
        !isNullableString(customer.email) ||
        !isNullableString(customer.address) ||
        !isNullableString(customer.tax_code) ||
        !isNullableString(customer.birth_date) ||
        !isNullablePositiveInteger(customer.customer_group_id) ||
        !isNullablePositiveInteger(customer.membership_tier_id) ||
        !isNonNegativeNumber(customer.credit_limit) ||
        !isFiniteNumber(customer.points_balance) ||
        !isFiniteNumber(customer.debt_balance) ||
        typeof customer.status !== 'boolean' ||
        !isNullableString(customer.note) ||
        (customer.created_at !== null && !isTimestamp(customer.created_at))
      ) {
        invalidBundle()
      }
      customerIds.add(customer.id)
    }

    const staffIds = new Set<number>()
    for (const member of bundle.staff ?? []) {
      if (
        !isPositiveInteger(member.id) ||
        staffIds.has(member.id) ||
        !isNullableString(member.employee_code) ||
        !isNonEmptyString(member.full_name) ||
        !isNullableString(member.phone) ||
        !isNonNegativeNumber(member.points_balance) ||
        !isNonEmptyString(member.status)
      ) {
        invalidBundle()
      }
      staffIds.add(member.id)
    }

    const bookingIds = new Set<number>()
    for (const booking of bundle.bookings ?? []) {
      if (
        !isPositiveInteger(booking.id) ||
        bookingIds.has(booking.id) ||
        !isNonEmptyString(booking.booking_code) ||
        booking.branch_id !== branchId ||
        !isNullablePositiveInteger(booking.customer_id) ||
        !isNullableString(booking.customer_name) ||
        !isNonEmptyString(booking.customer_phone) ||
        !isTimestamp(booking.arrival_time) ||
        !isTimestamp(booking.expected_end_at) ||
        !isPositiveInteger(booking.guest_count) ||
        !isPositiveInteger(booking.duration) ||
        !['pending', 'confirmed', 'seated', 'cancelled'].includes(booking.status) ||
        !isNonNegativeNumber(booking.deposit_amount) ||
        ![null, 'cash', 'bank_transfer'].includes(booking.deposit_method) ||
        !isNullableString(booking.note) ||
        !['call', 'store', 'app'].includes(booking.source) ||
        !isNullableString(booking.reason) ||
        (booking.seated_at !== null && !isTimestamp(booking.seated_at)) ||
        (booking.cancelled_at !== null && !isTimestamp(booking.cancelled_at)) ||
        !isPositiveInteger(booking.data_version) ||
        !Array.isArray(booking.tables) ||
        booking.tables.length === 0 ||
        !Array.isArray(booking.items) ||
        (booking.created_at !== null && !isTimestamp(booking.created_at)) ||
        (booking.updated_at !== null && !isTimestamp(booking.updated_at))
      ) {
        invalidBundle()
      }
      bookingIds.add(booking.id)

      const bookingTableIds = new Set<number>()
      let primaryCount = 0
      for (const table of booking.tables) {
        if (
          !isPositiveInteger(table.id) ||
          bookingTableIds.has(table.id) ||
          !tableIds.has(table.id) ||
          table.branch_id !== branchId ||
          !isPositiveInteger(table.area_id) ||
          !areaIds.has(table.area_id) ||
          !isNullableString(table.code) ||
          !isNonEmptyString(table.name) ||
          !isNonNegativeNumber(table.seats) ||
          typeof table.is_primary !== 'boolean' ||
          !isNullablePositiveInteger(table.session_id) ||
          (table.area !== null &&
            (!isPositiveInteger(table.area.id) ||
              !isNullableString(table.area.code) ||
              !isNonEmptyString(table.area.name)))
        ) {
          invalidBundle()
        }
        bookingTableIds.add(table.id)
        if (table.is_primary) primaryCount += 1
      }
      if (primaryCount !== 1) invalidBundle()

      const bookingItemIds = new Set<number>()
      for (const item of booking.items) {
        if (
          !isPositiveInteger(item.id) ||
          bookingItemIds.has(item.id) ||
          !isPositiveInteger(item.product_id) ||
          !isNullableString(item.product_name) ||
          !isNonNegativeNumber(item.quantity) ||
          (item.status !== undefined && !['pending', 'completed'].includes(item.status)) ||
          Number(item.quantity) <= 0 ||
          !isNullableString(item.note)
        ) {
          invalidBundle()
        }
        bookingItemIds.add(item.id)
      }
    }

    const sessionIds = new Set<number>()
    const sessionTableIds = new Set<number>()
    const orderIds = new Set<number>()
    const orderItemIds = new Set<number>()
    const expectedSessionPricing: OfflineBootstrapBundle['signed_payload']['session_pricing_snapshots'] =
      []
    const expectedSessionItemPrices: OfflineBootstrapBundle['signed_payload']['session_item_prices'] =
      []

    for (const snapshot of bundle.sessions) {
      const session = snapshot.session
      const baseline = session.pricing_snapshot.settlement_baseline
      if (
        !isPositiveInteger(snapshot.data_version) ||
        !isPositiveInteger(session.id) ||
        sessionIds.has(session.id) ||
        !isPositiveInteger(session.table_id) ||
        !tableIds.has(session.table_id) ||
        tableStatuses.get(session.table_id) !== 'playing' ||
        sessionTableIds.has(session.table_id) ||
        !isTimestamp(session.started_at) ||
        (session.ended_at !== null && !isTimestamp(session.ended_at)) ||
        !isNonNegativeNumber(session.total_minutes) ||
        !isNonNegativeNumber(session.table_amount) ||
        (session.booking_deposit_amount !== undefined &&
          !isNonNegativeNumber(session.booking_deposit_amount)) ||
        (session.booking_deposit_method !== undefined &&
          !isNullableString(session.booking_deposit_method)) ||
        (session.booking_deposit_player_client_id !== undefined &&
          session.booking_deposit_player_client_id !== null &&
          !isPositiveInteger(session.booking_deposit_player_client_id)) ||
        (session.booking_deposit_applied_amount !== undefined &&
          !isNonNegativeNumber(session.booking_deposit_applied_amount)) ||
        (session.last_transferred_at !== undefined &&
          session.last_transferred_at !== null &&
          !isTimestamp(session.last_transferred_at)) ||
        (session.table_transfers !== undefined && !Array.isArray(session.table_transfers)) ||
        !isNullablePositiveInteger(session.pricing_price_list_id) ||
        !isNullableString(session.pricing_price_list_name) ||
        !isNullablePositiveInteger(session.area_pricing_policy_id) ||
        !isNullableString(session.area_pricing_policy_name) ||
        !isNonEmptyString(session.pricing_source) ||
        !isRecord(session.pricing_snapshot) ||
        !isRecord(baseline) ||
        !isNonNegativeNumber(baseline.settled_table_amount) ||
        !isNonNegativeNumber(baseline.settled_discount_amount) ||
        !isNonNegativeNumber(baseline.settled_product_amount) ||
        !Array.isArray(session.calculation_segments) ||
        session.status !== 'playing' ||
        !isPositiveInteger(session.data_version) ||
        session.data_version !== snapshot.data_version ||
        !Array.isArray(snapshot.players) ||
        !Array.isArray(snapshot.items) ||
        !isNullablePositiveInteger(snapshot.order.id) ||
        !isNullableString(snapshot.order.note) ||
        (snapshot.order.table_discount_type !== undefined &&
          !['amount', 'percent'].includes(snapshot.order.table_discount_type)) ||
        (snapshot.order.table_discount_value !== undefined &&
          !isNonNegativeNumber(snapshot.order.table_discount_value)) ||
        !['amount', 'percent'].includes(snapshot.order.discount_type) ||
        !isNonNegativeNumber(snapshot.order.discount_value) ||
        (snapshot.order.voucher_code !== undefined &&
          !isNullableString(snapshot.order.voucher_code)) ||
        (snapshot.order.voucher_codes !== undefined &&
          (!Array.isArray(snapshot.order.voucher_codes) ||
            snapshot.order.voucher_codes.length > 20 ||
            snapshot.order.voucher_codes.some((code) => !isNonEmptyString(code)) ||
            new Set(snapshot.order.voucher_codes).size !== snapshot.order.voucher_codes.length)) ||
        (snapshot.order.voucher_discount_amount !== undefined &&
          !isNonNegativeNumber(snapshot.order.voucher_discount_amount)) ||
        !isNonNegativeNumber(snapshot.order.discount_amount) ||
        !isNonNegativeNumber(snapshot.order.subtotal) ||
        !isNonNegativeNumber(snapshot.order.total_amount)
      ) {
        invalidBundle()
      }
      sessionIds.add(session.id)
      sessionTableIds.add(session.table_id)
      if (snapshot.order.id !== null) {
        if (orderIds.has(snapshot.order.id)) {
          invalidBundle()
        }
        orderIds.add(snapshot.order.id)
      }

      for (const segment of session.calculation_segments) {
        if (
          !isTimestamp(segment.start_at) ||
          !isTimestamp(segment.end_at) ||
          !isNonNegativeNumber(segment.minutes) ||
          !isNonNegativeNumber(segment.price_per_hour) ||
          (segment.price_unit !== undefined && !isNonEmptyString(segment.price_unit)) ||
          (segment.price_amount !== undefined && !isNonNegativeNumber(segment.price_amount)) ||
          (segment.price_per_minute !== undefined &&
            !isNonNegativeNumber(segment.price_per_minute)) ||
          (segment.is_prepaid !== undefined && typeof segment.is_prepaid !== 'boolean') ||
          (segment.is_transfer_boundary !== undefined &&
            typeof segment.is_transfer_boundary !== 'boolean') ||
          !isNonNegativeNumber(segment.amount) ||
          !isNullablePositiveInteger(segment.matched_rule_id) ||
          !isNullableString(segment.matched_rule_name) ||
          !isNonEmptyString(segment.pricing_source)
        ) {
          invalidBundle()
        }
      }

      for (const segment of session.transferred_calculation_segments ?? []) {
        if (
          !isTimestamp(segment.start_at) ||
          !isTimestamp(segment.end_at) ||
          !isNonNegativeNumber(segment.minutes) ||
          !isNonNegativeNumber(segment.price_per_hour) ||
          (segment.price_unit !== undefined && !isNonEmptyString(segment.price_unit)) ||
          (segment.price_amount !== undefined && !isNonNegativeNumber(segment.price_amount)) ||
          (segment.price_per_minute !== undefined &&
            !isNonNegativeNumber(segment.price_per_minute)) ||
          (segment.is_prepaid !== undefined && typeof segment.is_prepaid !== 'boolean') ||
          (segment.is_transfer_boundary !== undefined &&
            typeof segment.is_transfer_boundary !== 'boolean') ||
          !isNonNegativeNumber(segment.amount) ||
          !isNullablePositiveInteger(segment.matched_rule_id) ||
          !isNullableString(segment.matched_rule_name) ||
          !isNonEmptyString(segment.pricing_source)
        ) {
          invalidBundle()
        }
      }

      for (const transfer of session.table_transfers ?? []) {
        if (
          (transfer.source_table_name !== undefined &&
            !isNullableString(transfer.source_table_name)) ||
          (transfer.target_table_name !== undefined &&
            !isNullableString(transfer.target_table_name)) ||
          (transfer.started_at !== null &&
            transfer.started_at !== undefined &&
            !isTimestamp(transfer.started_at)) ||
          (transfer.transferred_at !== null &&
            transfer.transferred_at !== undefined &&
            !isTimestamp(transfer.transferred_at)) ||
          (transfer.amount !== undefined && !isNonNegativeNumber(transfer.amount))
        ) {
          invalidBundle()
        }
      }

      const playerIds = new Set<number>()
      for (const player of snapshot.players) {
        if (
          !isPositiveInteger(player.client_id) ||
          playerIds.has(player.client_id) ||
          !isNonEmptyString(player.name) ||
          !isNullableString(player.phone) ||
          !isNullablePositiveInteger(player.customer_id) ||
          !isFiniteNumber(player.sort_order) ||
          (player.joined_at != null && !isTimestamp(player.joined_at)) ||
          (player.left_at != null && !isTimestamp(player.left_at)) ||
          (player.settled_at != null && !isTimestamp(player.settled_at))
        ) {
          invalidBundle()
        }
        playerIds.add(player.client_id)
      }

      for (const item of snapshot.items) {
        const unitlessItem = item.unit_id === null
        if (
          !isPositiveInteger(item.id) ||
          orderItemIds.has(item.id) ||
          !isPositiveInteger(item.product_id) ||
          (!unitlessItem && !isPositiveInteger(item.unit_id)) ||
          (item.price_list_id != null && !isPositiveInteger(item.price_list_id)) ||
          !isNonNegativeNumber(item.quantity) ||
          !isNonNegativeNumber(item.original_unit_price) ||
          !isNonNegativeNumber(item.unit_price) ||
          !['amount', 'percent'].includes(item.discount_type) ||
          !isNonNegativeNumber(item.discount_value) ||
          !isNonNegativeNumber(item.discount_amount) ||
          !isNonNegativeNumber(item.vat_rate) ||
          !isNonNegativeNumber(item.vat_amount) ||
          !isNonNegativeNumber(item.line_total) ||
          !isNullableString(item.note) ||
          !isNullablePositiveInteger(item.player_client_id) ||
          (item.player_client_id !== null && !playerIds.has(item.player_client_id)) ||
          !isFiniteNumber(item.sort_order) ||
          !isNonEmptyString(item.product_name) ||
          !isNullableString(item.product_sku) ||
          (unitlessItem ? item.unit_name !== null : !isNonEmptyString(item.unit_name)) ||
          typeof item.track_inventory !== 'boolean'
        ) {
          invalidBundle()
        }
        orderItemIds.add(item.id)
        expectedSessionItemPrices.push({
          session_id: session.id,
          order_item_id: item.id,
          product_id: item.product_id,
          unit_id: item.unit_id,
          price_list_id: item.price_list_id ?? null,
          original_unit_price: item.original_unit_price,
          track_inventory: item.track_inventory,
        })
      }

      expectedSessionPricing.push({
        session_id: session.id,
        table_id: session.table_id,
        pricing_snapshot: session.pricing_snapshot,
      })
    }

    for (const [tableId, status] of tableStatuses) {
      if ((status === 'playing') !== sessionTableIds.has(tableId)) {
        invalidBundle()
      }
    }

    const catalogKeys = new Set<string>()
    const expectedCatalogPrices: OfflineBootstrapBundle['signed_payload']['catalog_prices'] = []
    const signedCatalogHasPriceListId = signed.catalog_prices.some(
      (entry) => isRecord(entry) && 'price_list_id' in entry,
    )
    for (const item of bundle.catalog) {
      const key = `${item.table_id}:${item.product_id}`
      const unitlessCombo = item.type === 'combo' && item.unit_id === null
      if (
        !isPositiveInteger(item.table_id) ||
        !tableIds.has(item.table_id) ||
        !isPositiveInteger(item.product_id) ||
        catalogKeys.has(key) ||
        !isNonEmptyString(item.type) ||
        !isNullableString(item.sku) ||
        !isNullableString(item.code) ||
        !isNonEmptyString(item.name) ||
        !isNullablePositiveInteger(item.category_id) ||
        !isNullableString(item.category_name) ||
        (!unitlessCombo && !isPositiveInteger(item.unit_id)) ||
        (unitlessCombo ? item.unit_name !== null : !isNonEmptyString(item.unit_name)) ||
        !isNonNegativeNumber(item.effective_price) ||
        !isFiniteNumber(item.current_stock) ||
        !isFiniteNumber(item.inventory) ||
        (item.ordered_quantity !== undefined && !isNonNegativeNumber(item.ordered_quantity)) ||
        typeof item.track_inventory !== 'boolean' ||
        typeof item.allow_discount !== 'boolean' ||
        item.sellable !== true ||
        typeof item.price_includes_vat !== 'boolean' ||
        !isNonNegativeNumber(item.vat_rate) ||
        !isNullableString(item.resolved_image_url)
      ) {
        invalidBundle()
      }
      catalogKeys.add(key)
      if (unitlessCombo && Array.isArray(item.sale_units) && item.sale_units.length > 0) {
        invalidBundle()
      }
      const saleUnits =
        Array.isArray(item.sale_units) && item.sale_units.length > 0
          ? item.sale_units
          : [{ unit_id: item.unit_id, sale_price: item.effective_price }]
      for (const unit of saleUnits) {
        if (
          (!unitlessCombo && !isPositiveInteger(unit.unit_id)) ||
          (unitlessCombo && unit.unit_id !== null) ||
          !isNonNegativeNumber(unit.sale_price)
        ) {
          invalidBundle()
        }
        expectedCatalogPrices.push({
          table_id: item.table_id,
          product_id: item.product_id,
          unit_id: unit.unit_id,
          ...(signedCatalogHasPriceListId ? { price_list_id: null } : {}),
          effective_price: unit.sale_price,
          allow_discount: item.allow_discount,
          track_inventory: item.track_inventory,
          sellable: true,
        })
        for (const [priceListId, prices] of Object.entries(item.price_list_prices ?? {})) {
          const price = prices[String(unit.unit_id)]
          if (price !== undefined) {
            if (signedCatalogHasPriceListId)
              expectedCatalogPrices.push({
                table_id: item.table_id,
                product_id: item.product_id,
                unit_id: unit.unit_id,
                price_list_id: Number(priceListId),
                effective_price: price,
                allow_discount: item.allow_discount,
                track_inventory: item.track_inventory,
                sellable: true,
              })
          }
        }
      }
    }

    if (
      !deepEqual(expectedSessionPricing, signed.session_pricing_snapshots) ||
      !sessionItemPricesEqual(expectedSessionItemPrices, signed.session_item_prices) ||
      !deepEqual(expectedCatalogPrices, signed.catalog_prices)
    ) {
      invalidBundle()
    }
  } catch (error) {
    if (error instanceof Error && error.message === 'OFFLINE_BUNDLE_INVALID') {
      throw error
    }
    invalidBundle()
  }
}

function assertBundleScope(
  contextKey: string,
  bundle: OfflineBootstrapBundle,
  context: OfflinePosContext,
): void {
  if (
    !isNonEmptyString(contextKey) ||
    !isNonEmptyString(context.tenant_code) ||
    !isPositiveInteger(context.branch_id) ||
    !isNonEmptyString(context.device_id) ||
    context.context_key !== contextKey ||
    bundle.tenant_code !== context.tenant_code ||
    bundle.branch.id !== context.branch_id ||
    context.schema_version !== bundle.schema_version ||
    context.pricing_contract_version !== bundle.pricing_contract_version ||
    contextKey !== buildOfflineContextKey(context.tenant_code, context.branch_id, context.device_id)
  ) {
    invalidBundle()
  }
}

export class OfflineBootstrapService {
  constructor(
    private readonly database: BigSaleDB,
    private readonly downloader: OfflineBootstrapDownloader = () =>
      http.get<ApiResponseEnvelope<OfflineBootstrapBundle>>(
        buildApiPath(apiEndpoints.offlinePos.bootstrap),
      ),
    private readonly randomUUID: () => string = () => crypto.randomUUID(),
  ) {}

  async download(): Promise<OfflineBootstrapBundle> {
    const response = await this.downloader()
    const candidate =
      isRecord(response) && Object.prototype.hasOwnProperty.call(response, 'data')
        ? response.data
        : response

    if (!isRecord(candidate)) {
      invalidBundle()
    }

    const bundle = candidate as unknown as OfflineBootstrapBundle
    assertCompatibleBundle(bundle)

    return bundle
  }

  async activate(contextKey: string, bundle: OfflineBootstrapBundle): Promise<void> {
    assertCompatibleBundle(bundle)
    const context = await this.database.offlineContexts.get(contextKey)

    if (context === undefined) {
      throw new Error('OFFLINE_CONTEXT_MISSING')
    }

    assertBundleScope(contextKey, bundle, context)

    const sessionLocalIds = new Map(
      bundle.sessions.map((snapshot) => {
        const localId = this.randomUUID()
        if (!UUID_PATTERN.test(localId)) invalidBundle()

        return [snapshot.session.id, localId] as const
      }),
    )
    const bookingLocalIds = new Map(
      (bundle.bookings ?? []).map((booking) => {
        const localId = this.randomUUID()
        if (!UUID_PATTERN.test(localId)) invalidBundle()
        return [booking.id, localId] as const
      }),
    )

    const tables: OfflineSalesTable[] = bundle.tables.map((table) => ({
      context_key: contextKey,
      id: table.id,
      branch_id: table.branch_id,
      area_id: table.area_id,
      code: table.code,
      name: table.name,
      seats: table.seats,
      status: table.status,
      sort_order: table.sort_order,
      use_area_pricing: table.use_area_pricing,
      area_pricing_policy_override_id: table.area_pricing_policy_override_id,
    }))
    const areas: OfflineArea[] = bundle.areas.map((area) => ({
      context_key: contextKey,
      id: area.id,
      branch_id: area.branch_id,
      code: area.code,
      name: area.name,
      icon: area.icon ?? null,
      status: area.status,
      sort_order: area.sort_order,
      price_list_id: area.price_list_id,
      area_pricing_policy_id: area.area_pricing_policy_id,
    }))
    const sessions: OfflineSession[] = bundle.sessions.map((snapshot) => ({
      context_key: contextKey,
      local_id: sessionLocalIds.get(snapshot.session.id)!,
      server_id: snapshot.session.id,
      table_id: snapshot.session.table_id,
      booking_id: snapshot.session.booking_id ?? null,
      started_at: snapshot.session.started_at,
      ended_at: snapshot.session.ended_at,
      total_minutes: snapshot.session.total_minutes,
      table_amount: snapshot.session.table_amount,
      booking_deposit_amount: snapshot.session.booking_deposit_amount ?? 0,
      booking_deposit_method: snapshot.session.booking_deposit_method ?? null,
      booking_deposit_player_client_id: snapshot.session.booking_deposit_player_client_id ?? null,
      booking_deposit_applied_amount: snapshot.session.booking_deposit_applied_amount ?? 0,
      ...(snapshot.session.transferred_table_amount === undefined
        ? {}
        : {
            transferred_table_amount: snapshot.session.transferred_table_amount,
            transferred_calculation_segments: structuredClone(
              snapshot.session.transferred_calculation_segments ?? [],
            ),
            transferred_player_breakdowns: structuredClone(
              snapshot.session.transferred_player_breakdowns ?? {},
            ),
            last_transferred_at: snapshot.session.last_transferred_at ?? null,
            table_transfers: structuredClone(snapshot.session.table_transfers ?? []),
          }),
      pricing_price_list_id: snapshot.session.pricing_price_list_id,
      pricing_price_list_name: snapshot.session.pricing_price_list_name,
      area_pricing_policy_id: snapshot.session.area_pricing_policy_id,
      area_pricing_policy_name: snapshot.session.area_pricing_policy_name,
      pricing_source: snapshot.session.pricing_source,
      pricing_snapshot: structuredClone(snapshot.session.pricing_snapshot),
      calculation_segments: snapshot.session.calculation_segments.map((segment) =>
        structuredClone(segment),
      ),
      status: snapshot.session.status,
      data_version: snapshot.session.data_version,
      bootstrap_id: bundle.bundle_id,
      pricing_contract_version: bundle.pricing_contract_version,
      pricing_signature: bundle.signature,
    }))
    const orders: OfflineOrder[] = bundle.sessions.map((snapshot) => ({
      context_key: contextKey,
      session_local_id: sessionLocalIds.get(snapshot.session.id)!,
      session_server_id: snapshot.session.id,
      order_server_id: snapshot.order.id,
      data_version: snapshot.data_version,
      players: snapshot.players.map((player) => ({
        client_id: player.client_id,
        name: player.name,
        phone: player.phone,
        customer_id: player.customer_id,
        sort_order: player.sort_order,
        joined_at: player.joined_at ?? snapshot.session.started_at,
        left_at: player.left_at ?? null,
        settled_at: player.settled_at ?? null,
      })),
      note: snapshot.order.note,
      table_discount_type: snapshot.order.table_discount_type ?? 'amount',
      table_discount_value: snapshot.order.table_discount_value ?? 0,
      discount_type: snapshot.order.discount_type,
      discount_value: snapshot.order.discount_value,
      voucher_codes: structuredClone(snapshot.order.voucher_codes ?? []),
      voucher_discount_amount: snapshot.order.voucher_discount_amount ?? 0,
      discount_amount: snapshot.order.discount_amount,
      subtotal: snapshot.order.subtotal,
      total_amount: snapshot.order.total_amount,
      items: snapshot.items.map((item) => ({
        local_id: (() => {
          const localId = this.randomUUID()
          if (!UUID_PATTERN.test(localId)) invalidBundle()

          return localId
        })(),
        server_id: item.id,
        product_id: item.product_id,
        unit_id: item.unit_id,
        ...(item.price_list_id === undefined ? {} : { price_list_id: item.price_list_id }),
        quantity: item.quantity,
        status: item.status ?? 'pending',
        original_unit_price: item.original_unit_price,
        unit_price: item.unit_price,
        discount_type: item.discount_type,
        discount_value: item.discount_value,
        discount_amount: item.discount_amount,
        vat_rate: item.vat_rate,
        vat_amount: item.vat_amount,
        line_total: item.line_total,
        note: item.note,
        player_client_id: item.player_client_id,
        ...(item.charge_group === undefined &&
        item.shared_player_client_ids === undefined &&
        item.source_session_id === undefined &&
        item.locked_at === undefined
          ? {}
          : {
              charge_group: item.charge_group ?? null,
              shared_player_client_ids: structuredClone(item.shared_player_client_ids ?? null),
              source_session_id: item.source_session_id ?? null,
              locked_at: item.locked_at ?? null,
            }),
        sort_order: item.sort_order,
        product_name: item.product_name,
        product_sku: item.product_sku,
        unit_name: item.unit_name,
        track_inventory: item.track_inventory,
      })),
      updated_at: bundle.generated_at,
    }))
    const catalog: OfflineCatalogItem[] = bundle.catalog.map((item) => ({
      context_key: contextKey,
      table_id: item.table_id,
      product_id: item.product_id,
      type: item.type,
      sku: item.sku,
      code: item.code,
      name: item.name,
      category_id: item.category_id,
      category_name: item.category_name,
      unit_id: item.unit_id,
      unit_name: item.unit_name,
      ...(item.sale_units && item.sale_units.length > 0 ? { sale_units: item.sale_units } : {}),
      ...(item.price_list_prices
        ? { price_list_prices: structuredClone(item.price_list_prices) }
        : {}),
      effective_price: item.effective_price,
      current_stock: item.current_stock,
      inventory: item.inventory,
      ordered_quantity: item.ordered_quantity ?? 0,
      track_inventory: item.track_inventory,
      allow_discount: item.allow_discount,
      sellable: item.sellable,
      price_includes_vat: item.price_includes_vat,
      vat_rate: item.vat_rate,
      resolved_image_url: item.resolved_image_url,
    }))
    const pricingSnapshots: OfflinePricingSnapshot[] = bundle.pricing_snapshots.map((snapshot) => ({
      context_key: contextKey,
      table_id: snapshot.table_id,
      bootstrap_id: snapshot.bootstrap_id,
      pricing_snapshot: structuredClone(snapshot.pricing_snapshot),
    }))
    const bookings: OfflineBooking[] = (bundle.bookings ?? []).map((booking) => ({
      context_key: contextKey,
      local_id: bookingLocalIds.get(booking.id)!,
      server_id: booking.id,
      base_data_version: booking.data_version,
      booking_code: booking.booking_code,
      branch_id: booking.branch_id,
      customer_id: booking.customer_id,
      customer_name: booking.customer_name,
      customer_phone: booking.customer_phone,
      arrival_time: booking.arrival_time,
      expected_end_at: booking.expected_end_at,
      guest_count: booking.guest_count,
      duration: booking.duration,
      status: booking.status,
      deposit_amount: booking.deposit_amount,
      deposit_method: booking.deposit_method,
      note: booking.note,
      source: booking.source,
      reason: booking.reason,
      seated_at: booking.seated_at,
      cancelled_at: booking.cancelled_at,
      data_version: booking.data_version,
      tables: structuredClone(booking.tables),
      items: structuredClone(booking.items),
      created_at: booking.created_at,
      updated_at: booking.updated_at,
    }))
    const customers = bundle.customers.map((customer) => ({
      id: customer.id,
      code: customer.code,
      name: customer.name,
      phone: customer.phone,
      phone_normalized: customer.phone_normalized,
      email: customer.email,
      address: customer.address,
      tax_code: customer.tax_code,
      birth_date: customer.birth_date,
      customer_group_id: customer.customer_group_id,
      membership_tier_id: customer.membership_tier_id,
      group: structuredClone(customer.group),
      membership_tier: structuredClone(customer.membership_tier),
      credit_limit: customer.credit_limit,
      points_balance: customer.points_balance,
      debt_balance: customer.debt_balance,
      status: customer.status,
      note: customer.note,
      created_at: customer.created_at,
    }))
    const staff = (bundle.staff ?? []).map((member) => ({
      id: member.id,
      employee_code: member.employee_code,
      full_name: member.full_name,
      phone: member.phone,
      points_balance: member.points_balance,
      status: member.status,
    }))
    const vouchers: OfflineVoucher[] = bundle.vouchers.map((voucher) => ({
      ...structuredClone(voucher),
      context_key: contextKey,
    }))
    const serverTimeOffset = Date.parse(bundle.server_time) - Date.now()

    await this.database.transaction(
      'rw',
      [
        this.database.offlineContexts,
        this.database.areas,
        this.database.salesTables,
        this.database.sessions,
        this.database.orders,
        this.database.bookings,
        this.database.catalog,
        this.database.pricingSnapshots,
        this.database.vouchers,
        this.database.outboxCommands,
      ],
      async () => {
        const transactionalContext = await this.database.offlineContexts.get(contextKey)
        if (transactionalContext === undefined) {
          throw new Error('OFFLINE_CONTEXT_MISSING')
        }
        assertBundleScope(contextKey, bundle, transactionalContext)

        const unresolved = await this.database.outboxCommands
          .where('[context_key+status]')
          .anyOf(
            (['pending', 'syncing', 'failed', 'blocked'] as const).map((status) => [
              contextKey,
              status,
            ]),
          )
          .count()

        if (unresolved > 0) {
          throw new Error('OFFLINE_PENDING_COMMANDS')
        }

        await Promise.all([
          this.database.areas.where('context_key').equals(contextKey).delete(),
          this.database.salesTables.where('context_key').equals(contextKey).delete(),
          this.database.sessions.where('context_key').equals(contextKey).delete(),
          this.database.orders.where('context_key').equals(contextKey).delete(),
          this.database.bookings.where('context_key').equals(contextKey).delete(),
          this.database.catalog.where('context_key').equals(contextKey).delete(),
          this.database.pricingSnapshots.where('context_key').equals(contextKey).delete(),
          this.database.vouchers
            .where('[context_key+status]')
            .equals([contextKey, 'active'])
            .delete(),
        ])
        await this.database.areas.bulkPut(areas)
        await this.database.salesTables.bulkPut(tables)
        await this.database.sessions.bulkPut(sessions)
        await this.database.orders.bulkPut(orders)
        await this.database.bookings.bulkPut(bookings)
        await this.database.catalog.bulkPut(catalog)
        await this.database.pricingSnapshots.bulkPut(pricingSnapshots)
        await this.database.vouchers.bulkPut(vouchers)
        const updated = await this.database.offlineContexts.update(contextKey, {
          bootstrap_id: bundle.bundle_id,
          bootstrap_at: bundle.generated_at,
          actor_user_id: bundle.actor_user_id,
          permission_codes: [...bundle.permission_codes],
          branch_timezone: bundle.branch_timezone,
          server_time_offset_ms: serverTimeOffset,
          signed_pricing_payload: structuredClone(bundle.signed_payload),
          pricing_signature: bundle.signature,
          customers,
          // Older backends omit `staff`; keeping it undefined lets the POS screens
          // fall back to the live staff API instead of showing an empty staff list.
          ...(bundle.staff === undefined ? {} : { staff }),
          bank_accounts: structuredClone(bundle.bank_accounts ?? []),
          print_templates: structuredClone(bundle.print_templates ?? []),
          price_lists: structuredClone(bundle.price_lists ?? []),
          // Bundles from older backends omit these; leaving them undefined lets the
          // POS screens fall back to the live APIs instead of showing empty data.
          ...(bundle.product_groups === undefined
            ? {}
            : { product_groups: structuredClone(bundle.product_groups) }),
          ...(bundle.payment_methods === undefined
            ? {}
            : { payment_methods: structuredClone(bundle.payment_methods) }),
          // Older backends omit `point_policy`; leaving it undefined lets the payment
          // form fall back to the live API instead of silently hiding point redemption.
          ...(bundle.point_policy === undefined
            ? {}
            : { point_policy: structuredClone(bundle.point_policy) }),
        })

        if (updated !== 1) {
          throw new Error('OFFLINE_CONTEXT_MISSING')
        }
      },
    )
  }
}
